import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Modal } from "antd";
import { orderBy } from "lodash";
import { useHistory } from "react-router-dom";
import BannerTop from "../../components/BannerTop/BannerTop";
import Tabs from "../../components/Tabs/Tabs";
import CompanyList from "../../components/List/Company.list";
import ContactList from "../../components/List/Contact.list";
import PitchList from "../../components/List/Pitch.list";
import PitchScoreList from "../../components/List/PitchScore.list";
import TagOptions from "../../components/Tag/TagOptions";
import "./tag.style.scss";
import Localize, { l } from "../../libs/localization";
import { getFromLocalStorage } from "../../libs/helpers/localstorage";
import usePermissions from "../../hooks/usePermissions";
import LimitedFeatureBanner from "../../design-library/LimitedFeatureBanner/LimitedFeatureBanner.component";
import useTags from "../../hooks/useTags";
import useTagAssociations from "../../hooks/useTagAssociations";
import { exportTagCompaniesApi, TAG_ENTITY } from "../../api/tag.api";
import getPluginConsumption from "../../libs/getPluginConsumption";
import getUserPlugins from "../../libs/getUserPlugins";
import { exportTagContactsAction } from "../../actions/tag.actions";
import {
  EXPORT_COMPANIES_PLUGIN,
  EXPORT_CONTACTS_PLUGIN,
} from "../../api/user.api";
import useUser from "../../hooks/useUser";
import ErrorModal from "../../components/ErrorModal";
import { downloadBlob } from "../../libs/download";

const PAGE_SIZE = 50;
const { confirm } = Modal;

const tabToEntityMap = {
  0: TAG_ENTITY.COMPANY,
  1: TAG_ENTITY.CONTACT,
  2: TAG_ENTITY.PITCH,
  3: TAG_ENTITY.PITCH_SCORE,
};

function TagPage({ location }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useUser();
  const tenant = getFromLocalStorage("lefacWebTenant")?.data;

  const permissions = usePermissions();

  // Parse query parameters - these are the source of truth
  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const { selectedTag, selectedEntityTab, selectedEntity, currentPage } =
    useMemo(() => {
      const selectedEntityTab_ = urlParams.get("selectedEntity") || "0";
      return {
        selectedTag: urlParams.get("selectedTag"),
        selectedEntityTab: selectedEntityTab_,
        selectedEntity:
          tabToEntityMap[selectedEntityTab_] || TAG_ENTITY.COMPANY,
        currentPage: parseInt(urlParams.get("currentPage"), 10) || 1,
      };
    }, [location.search]);

  // Use the tags hook to fetch tag data and manage selected tag state
  const {
    tags,
    data,
    loading: tagsLoading,
    setSelectedTag: setSelectedTagInHook,
    pagination,
    reloadTags,
    reloadTagDetail,
    deleteTag: deleteTagFromHook,
  } = useTags({
    entity: selectedEntity,
    currentPage,
  });

  const entityIds = useMemo(() => data?.map((item) => item._id) || [], [data]);

  // Use the tag associations hook to get associated tag IDs for entities
  const { data: associatedTagsIds, loading: loadingTagAssociations } =
    useTagAssociations({
      entity: selectedEntity,
      entityIds,
    });

  console.log({ selectedEntity, entityIds, associatedTagsIds });

  const tagData = tags.find((tag) => tag._id === selectedTag) || null;

  // Update the hook's selectedTag whenever our URL selectedTag changes
  useEffect(() => {
    if (selectedTag !== undefined) {
      setSelectedTagInHook(selectedTag);
    }
  }, [selectedTag, setSelectedTagInHook]);

  // Function to update selected tag by updating the URL
  const setSelectedTag = useCallback(
    (tagId) => {
      if (tagId) {
        urlParams.set("selectedTag", tagId);
      } else {
        urlParams.delete("selectedTag");
      }

      // Reset to page 1 when changing tag
      urlParams.set("currentPage", "1");

      const newSearch = urlParams.toString();
      const newUrl = newSearch ? `/tag?${newSearch}` : "/tag";

      history.push(newUrl);
    },
    [urlParams, history],
  );

  // Function to update selected entity (tab) by updating the URL
  const setSelectedEntity = useCallback(
    (entity) => {
      urlParams.set("selectedEntity", entity);
      // Reset to page 1 when changing entity
      urlParams.set("currentPage", "1");

      const newSearch = urlParams.toString();
      const newUrl = `/tag?${newSearch}`;

      history.push(newUrl);
    },
    [urlParams, history],
  );

  // Function to update current page by updating the URL
  const setCurrentPage = useCallback(
    (pageNum) => {
      urlParams.set("currentPage", pageNum.toString());

      const newSearch = urlParams.toString();
      const newUrl = `/tag?${newSearch}`;

      history.push(newUrl);
    },
    [urlParams, history],
  );

  // Empty variables to be filled with remaining tag logic
  const loadingExport = false;

  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Empty functions to be filled with tag logic
  const searchRequest = () => {};
  const deleteTag = async () => {
    if (!selectedTag) return;

    confirm({
      title: Localize("TAG.CONFIRM_DELETE_TITLE"),
      content: Localize("TAG.CONFIRM_DELETE_DESC"),
      cancelText: Localize("COMMON.CANCEL"),
      okText: Localize("COMMON.DELETE"),
      onOk: async () => {
        try {
          await deleteTagFromHook(selectedTag);
          setSelectedTag(null);
        } catch (error) {
          ErrorModal.show(error);
        }
      },
    });
  };
  async function exportContacts() {
    const id = selectedTag;
    if (user.group === "SUPER-ADMIN" || user.group === "ADMIN")
      dispatch(exportTagContactsAction({ id }));
    else {
      let creditsLeft = 0;
      if (permissions.exportContactsV2) {
        const pluginName = "ExportContactsV2";
        const plugin = getUserPlugins(user).find((p) => p.name === pluginName);
        creditsLeft =
          (plugin?.data?.contactLimit || 0) -
          ((
            await getPluginConsumption({
              user,
              pluginName,
            })
          )?.contactExport?.count || 0);
      } else {
        const creditData = Object.values(
          user?.licence?.tenants[tenant]?.plugins,
        )?.find((plugin) => plugin.name === EXPORT_CONTACTS_PLUGIN)?.data;
        creditsLeft =
          (creditData?.contactLimit || 0) -
          parseInt(creditData?.exportedCount, 10);
      }
      const content =
        creditsLeft > 0
          ? `${Localize(
              "TAG.EXPORT_MODAL_DESCRIPTION",
            )} ${creditsLeft}. ${Localize("TAG.EXPORT_CONTINUE")}`
          : Localize("ERROR.ERROR_EXPORT_CONTACTS");
      confirm({
        title: Localize("TAG.EXPORT_MODAL_TITLE"),
        content,
        okButtonProps: { disabled: creditsLeft <= 0 },
        cancelText: Localize("COMMON.CANCEL"),
        onOk() {
          dispatch(exportTagContactsAction({ id }));
        },
      });
    }
    return null;
  }

  async function exportCompanies() {
    async function export_() {
      try {
        const result = await exportTagCompaniesApi({ id: selectedTag });
        const tagName = tags.find((t) => t._id === selectedTag)?.name;
        downloadBlob(result, `tag_${tagName || selectedTag}_companies.xlsx`);
      } catch (e) {
        ErrorModal.show(e);
      }
    }
    if (user.group === "SUPER-ADMIN" || user.group === "ADMIN") export_();
    else {
      let creditsLeft = 0;
      if (permissions.exportCompaniesV2) {
        const pluginName = "ExportCompaniesV2";
        const plugin = getUserPlugins(user).find((p) => p.name === pluginName);
        creditsLeft =
          (plugin?.data?.companyLimit || 0) -
          ((
            await getPluginConsumption({
              user,
              pluginName,
            })
          )?.companyExport?.count || 0);
      } else {
        const creditData = Object.values(
          user?.licence?.tenants[tenant]?.plugins,
        )?.find((plugin) => plugin.name === EXPORT_COMPANIES_PLUGIN)?.data;
        creditsLeft =
          (creditData?.contactLimit || 0) -
          parseInt(creditData?.exportedCount, 10);
      }
      const content =
        creditsLeft > 0
          ? `${Localize(
              "TAG.EXPORT_MODAL_DESCRIPTION",
            )} ${creditsLeft}. ${Localize("TAG.EXPORT_CONTINUE")}`
          : Localize("ERROR.ERROR_EXPORT_CONTACTS");
      confirm({
        title: Localize("TAG.EXPORT_MODAL_TITLE"),
        content,
        okButtonProps: { disabled: creditsLeft <= 0 },
        cancelText: Localize("COMMON.CANCEL"),
        onOk() {
          export_();
        },
      });
    }
    return null;
  }

  const getData = (entity) => {
    if (tagsLoading) return [];
    return (tabToEntityMap[selectedEntityTab] === entity && data) || [];
  };
  const getAssociations = (entity) => {
    if (loadingTagAssociations) return [];
    return (
      (tabToEntityMap[selectedEntityTab] === entity && associatedTagsIds) || []
    );
  };

  const renderContacts = () => {
    if (permissions.contact) {
      return (
        <ContactList
          data={getData(TAG_ENTITY.CONTACT)}
          title={
            <>
              {Localize("PAGES.CONTACTS.TITLE")} (
              {tagData ? tagData.contacts : 0})
            </>
          }
          total={pagination.totalResults}
          pageSize={pagination.pageSize}
          setPageSize={setPageSize}
          isTagPage
          loadingList={tagsLoading}
          loadingTag={loadingTagAssociations}
          associatedTagsIds={getAssociations(TAG_ENTITY.CONTACT)}
          setContactTagPage={(val) => setCurrentPage(val)}
          onTagRemove={() => {
            reloadTagDetail();
          }}
          page={pagination.currentPage}
        />
      );
    }
    return null;
  };

  const renderCompanies = () => {
    if (permissions.company) {
      return (
        <CompanyList
          data={getData(TAG_ENTITY.COMPANY)}
          title={
            <>
              {Localize("PAGES.COMPANIES.TITLE")} (
              {tagData ? tagData.companies : 0})
            </>
          }
          total={pagination.totalResults}
          pageSize={pagination.pageSize}
          setPageSize={setPageSize}
          isTagPage
          loadingList={tagsLoading}
          loadingTag={loadingTagAssociations}
          associatedTagsIds={getAssociations(TAG_ENTITY.COMPANY)}
          setCompanyTagPage={(val) => setCurrentPage(val)}
          onTagRemove={async () => {
            reloadTagDetail();
          }}
          page={pagination.currentPage}
        />
      );
    }
    return null;
  };

  const renderPitches = () => {
    if (permissions.pitches) {
      return (
        <PitchList
          pitchList={getData(TAG_ENTITY.PITCH)}
          title={
            <>
              {Localize("PAGES.PITCH.TITLE")} ({tagData ? tagData.pitches : 0})
            </>
          }
          total={pagination.totalResults}
          pageSize={pagination.pageSize}
          setPageSize={setPageSize}
          loadingList={tagsLoading}
          loadingTag={loadingTagAssociations}
          associatedTagsIds={getAssociations(TAG_ENTITY.PITCH)}
          setPitchTagPage={(val) => setCurrentPage(val)}
          onTagRemove={() => {
            reloadTagDetail();
          }}
          page={pagination.currentPage}
          isTagPage
        />
      );
    }
    return null;
  };

  const renderPitchScores = () => {
    if (permissions.pitchScores) {
      return (
        <PitchScoreList
          data={getData(TAG_ENTITY.PITCH_SCORE)}
          title={
            <>
              {Localize("PAGES.PITCH_SCORES.TITLE")} (
              {tagData ? tagData.pitchScores : 0})
            </>
          }
          total={pagination.totalResults}
          options={{
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
          }}
          setOptions={(options) => {
            if (options.page !== pagination.currentPage) {
              setCurrentPage(options.page);
            }
            if (options.pageSize !== pagination.pageSize) {
              setPageSize(options.pageSize);
            }
          }}
          loadingList={tagsLoading}
          loadingTag={loadingTagAssociations}
          associatedTagsIds={getAssociations(TAG_ENTITY.PITCH_SCORE)}
          onTagRemove={() => {
            reloadTagDetail();
          }}
        />
      );
    }
    return null;
  };

  return (
    <>
      <BannerTop small>
        <h1 className="mt-4 orange-title">{Localize("PAGES.TAG.TITLE")}</h1>
      </BannerTop>
      {}
      {permissions.favourites ? (
        <>
          <div className="searchPage__filters animation fadein-up slow">
            <TagOptions
              useId
              isForm
              value={selectedTag}
              tagList={orderBy(tags, "created_at", "desc")}
              onTagChange={(value) => setSelectedTag(value)}
              onTagSelect={searchRequest}
              onDeleteClick={deleteTag}
              deleteDisabled={!selectedTag}
              loadingExport={loadingExport}
              showExportContacts={
                selectedTag &&
                getData(TAG_ENTITY.CONTACT).length > 0 &&
                permissions.downloadContacts
              }
              showExportCompanies={
                selectedTag &&
                getData(TAG_ENTITY.COMPANY).length > 0 &&
                permissions.downloadCompanies
              }
              onExportContacts={exportContacts}
              onExportCompanies={exportCompanies}
            />
          </div>
          <Tabs
            containerClass="tagPage__tabs"
            options={{
              activeKey: selectedEntityTab,
              onChange: (key) => setSelectedEntity(key),
              onTabClick: () => {
                window.scrollTo({
                  behavior: "smooth",
                  left: 0,
                  top: 0,
                });
              },
              animated: true,
            }}
          >
            {renderCompanies()}
            {renderContacts()}
            {renderPitches()}
            {renderPitchScores()}
          </Tabs>
        </>
      ) : (
        <LimitedFeatureBanner message={l("ECOMMERCE.FAVOURITES_LIMIT_TEXT")} />
      )}
    </>
  );
}

export default TagPage;
